import React, { useCallback, useEffect, useState } from 'react';
import RefreshIcon from '../../ui/svg/common/refresh';

/* Usage:
-------------
const [simpleCaptchaCode, setSimpleCaptchaCode] = useState('');
const [simpleCaptchaAttempts, setSimpleCaptchaAttempts] = useState(0);

<SimpleCaptcha
    simpleCaptchaCode={ simpleCaptchaCode }
    setSimpleCaptchaCode={ setSimpleCaptchaCode }
    simpleCaptchaAttempts={ simpleCaptchaAttempts }
/>

OR version with validation
------------------------------------------------------------------
const simpleCaptchaControl = createControl('simpleCaptcha', '', {
    required: true
});

const [simpleCaptchaCode, setSimpleCaptchaCode] = useState<IFormControl>(simpleCaptchaControl);
const [simpleCaptchaAttempts, setSimpleCaptchaAttempts] = useState(0);

<div className="my-8">
    <SimpleCaptcha
        simpleCaptchaAttempts={ simpleCaptchaAttempts }
        simpleCaptchaCode={ simpleCaptchaCode.value }
        setSimpleCaptchaCode={ (value: string) => {
            setSimpleCaptchaCode((updateControl(simpleCaptchaCode, value)));
        }}
    />
</div>

<div className="text-red text-sm h-4 mb-4">
    {
        !simpleCaptchaCode.isValid && <div>{ simpleCaptchaCode.error }</div>
    }
</div>
*/

export interface SimpleCaptchaProps{
    index?: number,
    setSimpleCaptchaCode: Function,
    simpleCaptchaCode: string,
    simpleCaptchaAttempts: number,
}

/**
 * Client side validation error
 */
export const getSimpleCaptchaValidationError = () => {
    return 'Please enter the security code.';
};

/**
 * to reset China captcha, it should be re-rendered with a new timestamp
 */
const getChinaCaptchaTimestamp = () => {
    return (new Date()).getTime();
};

/**
 * generate captcha image source
 */
const getSrc = (timestamp: number, index?: number) => {
    return `/api/simplecaptcha/render?index=${ index ?? 0 }&t=${ timestamp }`;
};

/**
 * Simple captcha - used for China
 */
const SimpleCaptcha: React.FC<SimpleCaptchaProps> = (props) => {

    const [src, setSrc] = useState(getSrc(getChinaCaptchaTimestamp(), props.index));
    const { simpleCaptchaAttempts } = props;

    /**
     * refresh the captcha
     */
    const refresh = useCallback(() => {

        // rerender the image
        setSrc(getSrc(getChinaCaptchaTimestamp(), props.index));
    }, [props.index]);

    /**
     * re-render recaptcha on fail / ok
     */
    useEffect(() => {
        if(simpleCaptchaAttempts > 0) {
            refresh();
        }
    }, [simpleCaptchaAttempts, refresh]);

    return (
        <div>
            <div className="flex mb-2">
                <img
                    style={{
                        width: 'calc(100% - 60px)'
                    }}
                    src={ src }
                    alt="Captcha"
                    title="Captcha"
                />

                <button
                    type="button"
                    title="Refresh Captcha"
                    style={{
                        width: '60px'
                    }}
                    onClick={ refresh }
                    className="border border-gray-200-ho bg-white ml-4 p-4 rounded text-center">
                    <RefreshIcon width="20px" height="20px" color="rgb(74, 74, 74)" />
                </button>
            </div>

            <p className="mb-2 text-sm">Type the characters above:</p>

            <input
                type="text"
                className="border border-gray-200-ho bg-white outline-none w-full px-2 py-1"
                value={ props.simpleCaptchaCode }
                onChange={evt => {
                    props.setSimpleCaptchaCode(evt.target.value);
                }}
            />
        </div>
    )
};

export  default SimpleCaptcha;